import { useState, useCallback, useRef, useEffect } from 'react';
import { TOAST_DURATION } from '../constants/rugbyConstants';

/**
 * Custom hook for showing toast notifications
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.defaultDuration - Default duration in milliseconds
 * @returns {Object} Toast state and control functions
 */
const useToast = ({ defaultDuration = TOAST_DURATION.normal } = {}) => {
  const [toast, setToast] = useState({
    message: '',
    type: '',
    show: false,
    duration: defaultDuration
  });
  
  // Active timeout reference
  const toastTimeoutRef = useRef(null);

  /**
   * Hide the current toast
   */
  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, show: false }));
  }, []);

  /**
   * Show a toast notification
   * 
   * @param {string} message - Toast message to display
   * @param {string} type - Type of toast (success, error, info, warning)
   * @param {number} duration - Duration in milliseconds
   */
  const showToast = useCallback((message, type = '', duration = defaultDuration) => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // Set toast state
    setToast({
      message,
      type,
      show: true,
      duration
    });
    
    // Auto-hide toast after duration
    toastTimeoutRef.current = setTimeout(() => {
      hideToast();
    }, duration);
  }, [defaultDuration, hideToast]);

  /**
   * Show a success toast
   * 
   * @param {string} message - Toast message
   * @param {number} duration - Optional custom duration
   */
  const showSuccessToast = useCallback((message, duration) => {
    showToast(message, 'success', duration);
  }, [showToast]);

  /**
   * Show an error toast
   * 
   * @param {string} message - Toast message
   * @param {number} duration - Optional custom duration
   */
  const showErrorToast = useCallback((message, duration) => {
    showToast(message, 'error', duration);
  }, [showToast]);

  /**
   * Show an info toast
   * 
   * @param {string} message - Toast message
   * @param {number} duration - Optional custom duration
   */
  const showInfoToast = useCallback((message, duration) => {
    showToast(message, 'info', duration);
  }, [showToast]);

  /**
   * Show a warning toast
   * 
   * @param {string} message - Toast message
   * @param {number} duration - Optional custom duration
   */
  const showWarningToast = useCallback((message, duration) => {
    showToast(message, 'warning', duration);
  }, [showToast]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  return {
    toast,
    showToast,
    hideToast,
    showSuccessToast,
    showErrorToast,
    showInfoToast,
    showWarningToast
  };
};

export default useToast;